import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ArrowLeft, ChevronDown, ChevronRight, Link2, ListTree, Loader } from "lucide-react";
import React, { useMemo, useState } from "react";
import { FilterSchemaTile } from "./schema-tile";
import { FIELD_TYPE_LABELS, containsFilter } from "./tree-utils";
import { ANY_RECORD_PREFIX, type FilterSchema, type SchemaField, type SchemaRelation } from "./types";
import { useFilterObjects, useFilterSchema } from "./use-filter-schema";

export type FieldPick = { kind: "field"; path: string } | { kind: "related"; path: string };

type Hop = {
    relation: SchemaRelation;
    schema: FilterSchema;
};

/**
 * Pick what a condition tests: a field of the object, a field of an object it points to
 * (Registration › Email), or related records (Registrations › …), which turn the condition
 * into a « has at least one / has no » block.
 */
export function FieldPicker({
    rootModel,
    labels,
    onPick,
    allowRelated = true,
    relatedOnly = false,
    invalid,
    autoOpen,
    trigger,
    className,
    extraRootFields,
    filterField
}: {
    rootModel: string;
    /** Labels of the current path, empty when nothing is picked yet. */
    labels: string[];
    onPick: (pick: FieldPick) => void;
    allowRelated?: boolean;
    /** Only linked objects (to walk through) and related records: the « Add related records » entry. */
    relatedOnly?: boolean;
    invalid?: boolean;
    autoOpen?: boolean;
    /** Replaces the default trigger button. */
    trigger?: React.ReactNode;
    /** Classes of the default trigger button. */
    className?: string;
    /** Fields of the root object the caller can filter on although the schema does not list them. */
    extraRootFields?: { name: string; label: string }[];
    /** Only the fields it keeps (those a list can sort on), linked objects to reach them. */
    filterField?: (field: SchemaField) => boolean;
}) {
    const [open, setOpen] = useState(Boolean(autoOpen));
    const [hops, setHops] = useState<Hop[]>([]);
    const [search, setSearch] = useState("");
    const currentModel = hops.length ? hops[hops.length - 1].relation.model : rootModel;
    const { schema, loading, error } = useFilterSchema(open ? currentModel : null);
    const rootSchema = useFilterSchema(open ? rootModel : null).schema;
    // « Add related records » also offers every record of an object, tied by its own conditions.
    const objects = useFilterObjects(open && relatedOnly);

    const prefix = hops.map(hop => hop.relation.name).join("__");
    const pathOf = (name: string) => (prefix ? `${prefix}__${name}` : name);

    const fields = useMemo(() => {
        const own = (schema?.fields ?? [])
            .filter(field => !filterField || filterField(field))
            .map(field => ({ name: field.name, label: field.label, typeLabel: FIELD_TYPE_LABELS[field.type] as string | undefined }));
        if (hops.length || !extraRootFields?.length) return own;
        const extra = extraRootFields.filter(field => !own.some(other => other.name === field.name)).map(field => ({ ...field, typeLabel: undefined }));
        return [...own, ...extra].sort((a, b) => a.label.localeCompare(b.label));
    }, [schema, hops.length, extraRootFields, filterField]);
    const toOne = useMemo(() => (schema?.relations ?? []).filter(relation => relation.kind === "to_one"), [schema]);
    const toMany = useMemo(() => (schema?.relations ?? []).filter(relation => relation.kind === "to_many"), [schema]);

    const close = (next: boolean) => {
        setOpen(next);
        if (!next) {
            setHops([]);
            setSearch("");
        }
    };
    const pick = (value: FieldPick) => {
        onPick(value);
        close(false);
    };
    const drill = (relation: SchemaRelation) => {
        if (!schema) return;
        setHops(previous => [...previous, { relation, schema }]);
        setSearch("");
    };

    return (
        <Popover open={open} onOpenChange={close} modal>
            <PopoverTrigger asChild>
                {trigger ?? (
                    <Button
                        type="button"
                        variant="outline"
                        className={cn("h-8 min-w-[200px] max-w-full justify-between rounded-sm bg-white px-2 text-sm font-normal shadow-sm", invalid && "border-red-500", className)}
                        title={labels.length ? labels.join(" › ") : undefined}
                    >
                        {labels.length ? (
                            <span className="flex min-w-0 items-center gap-1 truncate">
                                {labels.map((label, index) => (
                                    <span key={`${label}-${index}`} className={cn("truncate", index < labels.length - 1 ? "text-gray-500" : "text-black")}>
                                        {label}
                                        {index < labels.length - 1 && <ChevronRight className="ml-1 inline h-3 w-3" />}
                                    </span>
                                ))}
                            </span>
                        ) : (
                            <span className="text-gray-500">Select a field</span>
                        )}
                        <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0 text-gray-500" />
                    </Button>
                )}
            </PopoverTrigger>
            <PopoverContent className="flex max-h-[var(--radix-popover-content-available-height)] w-[380px] flex-col p-0" align="start" collisionPadding={12}>
                <div className="flex shrink-0 items-center gap-2 border-b px-2 py-2">
                    {hops.length > 0 ? (
                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6" aria-label="Back" onClick={() => setHops(previous => previous.slice(0, -1))}>
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                    ) : (
                        rootSchema && <FilterSchemaTile schema={rootSchema} size="sm" />
                    )}
                    <div className="flex min-w-0 flex-wrap items-center gap-1 text-xs text-gray-600">
                        <button type="button" className={cn("hover:underline", hops.length === 0 && "font-medium text-black")} onClick={() => setHops([])}>
                            {rootSchema?.name ?? "…"}
                        </button>
                        {hops.map((hop, index) => (
                            <span key={hop.relation.name + index} className="flex items-center gap-1">
                                <ChevronRight className="h-3 w-3" />
                                <button
                                    type="button"
                                    className={cn("hover:underline", index === hops.length - 1 && "font-medium text-black")}
                                    onClick={() => setHops(previous => previous.slice(0, index + 1))}
                                >
                                    {hop.relation.label}
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
                <Command shouldFilter filter={containsFilter} className="min-h-0 flex-1">
                    <CommandInput placeholder={`Search ${schema?.name ?? ""} fields...`} value={search} onValueChange={setSearch} />
                    <CommandList className="max-h-[360px] min-h-0 flex-1">
                        {loading && (
                            <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
                                <Loader className="h-4 w-4 animate-spin" /> Loading...
                            </div>
                        )}
                        {error && <div className="p-3 text-sm text-gray-500">{error}</div>}
                        {!loading && !error && <CommandEmpty>No field found</CommandEmpty>}
                        {schema && hops.length > 0 && !relatedOnly && !filterField && (
                            <CommandGroup heading={hops[hops.length - 1].relation.label}>
                                <CommandItem value={`__record ${hops[hops.length - 1].relation.label}`} onSelect={() => pick({ kind: "field", path: prefix })}>
                                    <Link2 className="mr-2 h-4 w-4 text-gray-500" />
                                    <span>
                                        The {hops[hops.length - 1].relation.label.toLowerCase()} itself
                                        <span className="ml-1 text-xs text-gray-500">(is empty, is one of…)</span>
                                    </span>
                                </CommandItem>
                            </CommandGroup>
                        )}
                        {schema && !relatedOnly && fields.length > 0 && (
                            <CommandGroup heading="Fields">
                                {fields.map(field => (
                                    <CommandItem key={field.name} value={`${field.label} ${field.name}`} onSelect={() => pick({ kind: "field", path: pathOf(field.name) })}>
                                        <span className="truncate">{field.label}</span>
                                        <span className="ml-auto pl-2 text-xs text-gray-500">{field.typeLabel}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                        {schema && toOne.length > 0 && (
                            <CommandGroup heading="Linked objects">
                                {toOne.map(relation => (
                                    <CommandItem key={relation.name} value={`${relation.label} ${relation.name} linked`} onSelect={() => drill(relation)}>
                                        <span className="truncate">{relation.label}</span>
                                        <ChevronRight className="ml-auto h-4 w-4 flex-shrink-0 text-gray-500" />
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                        {schema && allowRelated && toMany.length > 0 && (
                            <CommandGroup heading="Related records">
                                {toMany.map(relation => (
                                    <CommandItem key={relation.name} value={`${relation.label} ${relation.name} related`} onSelect={() => pick({ kind: "related", path: pathOf(relation.name) })}>
                                        <ListTree className="mr-2 h-4 w-4 flex-shrink-0 text-gray-500" />
                                        <span className="flex min-w-0 flex-col">
                                            <span className="truncate">{relation.label}</span>
                                            {relation.description && <span className="truncate text-xs text-gray-500">{relation.description}</span>}
                                        </span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                        {relatedOnly && hops.length === 0 && objects && objects.length > 0 && (
                            <CommandGroup heading="Any record of">
                                {objects.map(object => (
                                    <CommandItem
                                        key={object.model}
                                        value={`any ${object.name} ${object.name_plural}`}
                                        onSelect={() => pick({ kind: "related", path: `${ANY_RECORD_PREFIX}${object.model}` })}
                                    >
                                        <FilterSchemaTile schema={object} size="sm" />
                                        <span className="ml-2 truncate">Any {object.name.toLowerCase()}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
                {allowRelated && <p className="shrink-0 border-t px-3 py-2 text-xs text-gray-500">Related records test a list: has at least one, has no, or has only records matching conditions.</p>}
            </PopoverContent>
        </Popover>
    );
}
